import { useState, useEffect, useRef, useCallback } from "react";
import {
  Plus,
  Menu,
  Copy,
  Volume2,
  WifiOff,
  Search,
  Zap,
  Globe,
  Mic,
  Square,
  Pause,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon,
  Send,
} from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLanguage } from "../contexts/LanguageContext.tsx";
import { supabase } from "../lib/supabaseClient.ts";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  language?: "en" | "st";
  provider?: string;
  sources?: string[];
  image?: string;
}

interface Chat {
  id: string;
  title: string;
  messages: Message[];
  createdAt: string;
  language: "en" | "st";
  userId?: string;
}

interface VoiceState {
  mode: "idle" | "listening" | "recording" | "processing";
  duration: number;
  isSupported: boolean;
}

interface TTSState {
  speaking: boolean;
  currentMessageId: string | null;
  voices: SpeechSynthesisVoice[];
  selectedVoice: string;
}

const detectLanguage = (text: string): "en" | "st" => {
  const cleanText = text.toLowerCase().trim();
  const sesothoWords = [
    "ke",
    "ea",
    "ho",
    "ha",
    "le",
    "ba",
    "se",
    "li",
    "tsa",
    "joang",
    "jwang",
    "poone",
    "koro",
    "likhomo",
    "mobu",
    "temo",
    "lijalo",
    "metsi",
    "pula",
    "jala",
    "kotula",
    "nosetsa",
    "sheba",
    "sebelisa",
    "ntate",
    "mme",
  ];
  const englishWords = [
    "how",
    "what",
    "when",
    "where",
    "why",
    "who",
    "which",
    "can",
    "should",
    "would",
    "the",
    "and",
    "or",
    "but",
    "in",
    "on",
    "at",
    "to",
    "for",
    "with",
    "by",
    "cattle",
    "diseases",
    "winter",
    "farming",
    "crops",
    "soil",
    "plant",
    "grow",
  ];

  const words = cleanText.split(/\s+/);
  const sesothoMatches = words.filter((word) =>
    sesothoWords.includes(word)
  ).length;
  const englishMatches = words.filter((word) =>
    englishWords.includes(word)
  ).length;

  if (englishMatches > 0 && sesothoMatches === 0) return "en";
  if (sesothoMatches > 0) return "st";
  return "en";
};

export default function ProductionAIAdvisor({ user }: { user: any }) {
  const { t } = useLanguage();
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string>("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [researchMode, setResearchMode] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState<"en" | "st">("en");
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);


  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [voiceState, setVoiceState] = useState<VoiceState>({
    mode: "idle",
    duration: 0,
    isSupported:
      "webkitSpeechRecognition" in window || "SpeechRecognition" in window,
  });
  const [recognition, setRecognition] = useState<any>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [ttsState, setTTSState] = useState<TTSState>({
    speaking: false,
    currentMessageId: null,
    voices: [],
    selectedVoice: "",
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);


  const displayMessage = useCallback((text: string, callback: () => void) => {
    // Instant display - no typing animation
    callback();
  }, []);

  // Save chats to database and localStorage
  const saveChats = useCallback(
    async (updatedChats: Chat[]) => {
      localStorage.setItem("agrosense-ai-chats", JSON.stringify(updatedChats));
      if (user?.id) {
        try {
          const currentChat = updatedChats.find((c) => c.id === currentChatId);
          if (currentChat) {
            await supabase.from("ai_chats").upsert({
              id: currentChat.id,
              user_id: user.id,
              title: currentChat.title,
              messages: currentChat.messages,
              language: currentChat.language,
              created_at: currentChat.createdAt,
              updated_at: new Date().toISOString(),
            });
          }
        } catch {
          console.log("Database save failed, using localStorage only");
        }
      }
    },
    [user, currentChatId]
  );

  const createNewChat = useCallback(async (): Promise<void> => {
    const newChat: Chat = {
      id: Date.now().toString(),
      title: currentLanguage === "st" ? "Puisano e Ncha" : "New Chat",
      messages: [],
      createdAt: new Date().toISOString(),
      language: currentLanguage,
      userId: user?.id,
    };

    const updatedChats = [newChat, ...chats];
    setChats(updatedChats);
    setCurrentChatId(newChat.id);
    
    // Save to localStorage immediately
    localStorage.setItem("agrosense-ai-chats", JSON.stringify(updatedChats));
    
    // Try to save to database if user is logged in
    if (user?.id) {
      try {
        await supabase.from("ai_chats").upsert({
          id: newChat.id,
          user_id: user.id,
          title: newChat.title,
          messages: newChat.messages,
          language: newChat.language,
          created_at: newChat.createdAt,
          updated_at: new Date().toISOString(),
        });
      } catch {
        console.log("Database save failed, using localStorage only");
      }
    }
  }, [currentLanguage, user?.id, chats]);

  // Initialize chats with database integration
  useEffect(() => {
    const initializeChats = async () => {
      if (user?.id) {
        try {
          // Try to load from database first
          const { data, error } = await supabase
            .from("ai_chats")
            .select("*")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false });

          if (!error && data && data.length > 0) {
            const formattedChats = data.map((chat) => ({
              id: chat.id,
              title: chat.title,
              messages: chat.messages || [],
              createdAt: chat.created_at,
              language: chat.language || "en",
              userId: chat.user_id,
            }));
            setChats(formattedChats);
            setCurrentChatId(formattedChats[0].id);
            setCurrentLanguage(formattedChats[0].language);
            return;
          }
        } catch {
          console.log("Database not available, using localStorage");
        }
      }

      // Fallback to localStorage
      const savedChats = localStorage.getItem("agrosense-ai-chats");
      if (savedChats) {
        const parsed = JSON.parse(savedChats);
        setChats(parsed);
        setCurrentChatId(parsed[0]?.id || "");
        if (parsed[0]?.language) {
          setCurrentLanguage(parsed[0].language);
        }
      } else {
        // Create initial chat inline to avoid dependency issues
        const newChat: Chat = {
          id: Date.now().toString(),
          title: "New Chat",
          messages: [],
          createdAt: new Date().toISOString(),
          language: "en",
          userId: user?.id,
        };
        setChats([newChat]);
        setCurrentChatId(newChat.id);
        localStorage.setItem("agrosense-ai-chats", JSON.stringify([newChat]));
      }
    };

    initializeChats();

    // Setup TTS voices
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      setTTSState((prev) => ({
        ...prev,
        voices,
        selectedVoice:
          voices.find((v) => v.lang.startsWith("en"))?.name ||
          voices[0]?.name ||
          "",
      }));
    };

    loadVoices();
    speechSynthesis.addEventListener("voiceschanged", loadVoices);

    // Listen for online/offline events
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      if (timerRef.current) clearInterval(timerRef.current);

      if (recognition) {
        recognition.stop();
      }
    };
  }, [user]);



  // Removed auto-scroll - let users control scrolling manually

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [input]);

  const currentChat = chats.find((c) => c.id === currentChatId);

  const updateChatTitle = async (chatId: string, title: string) => {
    const updatedChats = chats.map((chat) =>
      chat.id === chatId ? { ...chat, title } : chat
    );
    setChats(updatedChats);
    await saveChats(updatedChats);
  };

  const confirmDeleteChat = (chatId: string) => {
    setDeletingChatId(chatId);
  };

  const deleteChat = async (chatId: string) => {
    if (chats.length === 1) return;

    const updatedChats = chats.filter((c) => c.id !== chatId);
    setChats(updatedChats);

    if (currentChatId === chatId) {
      setCurrentChatId(updatedChats[0]?.id || "");
    }

    // Delete from database if user is logged in
    if (user?.id) {
      try {
        await supabase.from("ai_chats").delete().eq("id", chatId);
      } catch {
        console.log("Database delete failed");
      }
    }
    localStorage.setItem("agrosense-ai-chats", JSON.stringify(updatedChats));
    setDeletingChatId(null);
  };

  const stopGeneration = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && !image) || loading || !currentChat) return;

    const detectedLang = detectLanguage(input.trim());
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString(),
      language: detectedLang,
      image: image || undefined,
    };

    // Update chat with user message
    const updatedChats = chats.map((chat) =>
      chat.id === currentChatId
        ? {
            ...chat,
            messages: [...chat.messages, userMessage],
            language: detectedLang,
          }
        : chat
    );
    setChats(updatedChats);

    // Update title if first message
    if (currentChat.messages.length === 0) {
      const title =
        input.trim().slice(0, 30) + (input.trim().length > 30 ? "..." : "");
      await updateChatTitle(currentChatId, title);
    }

    setInput("");
    setImage(null);
    setLoading(true);

    try {
      const controller = new AbortController();
      setAbortController(controller);

      const backendUrl =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const chatHistory = currentChat.messages.slice(-3).map((msg) => ({
        role: msg.role === "assistant" ? "ai" : msg.role,
        content: msg.content.substring(0, 200),
      }));

      const response = (await Promise.race([
        fetch(`${backendUrl}/api/ai-advisor`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: userMessage.content,
            language: detectedLang,
            enableResearch: researchMode,
            chatHistory,
            image: userMessage.image,
          }),
          signal: controller.signal,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Frontend timeout")), 30000)
        ),
      ])) as Response;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const responseData = await response.json();
      
      // Instant AI response display
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: responseData.response || responseData.text || "",
        timestamp: new Date().toISOString(),
        language: detectedLang,
        provider: responseData.provider || "AI Service",
        sources: responseData.sources || [],
      };

      const finalChats = chats.map((chat) =>
        chat.id === currentChatId
          ? { ...chat, messages: [...chat.messages, userMessage, aiMessage] }
          : chat
      );
      setChats(finalChats);
      saveChats(finalChats);
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("AI generation stopped by user");
      } else {
        console.error("AI request failed:", error);
        // Add fallback message on error
        const fallbackMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content:
            detectedLang === "st"
              ? "Tšoarelo, ha ke khone ho araba hona joale. Ka kopo leka hape."
              : "Sorry, I cannot respond right now. Please try again.",
          timestamp: new Date().toISOString(),
          language: detectedLang,
          provider: "Error Handler",
          sources: [],
        };

        const errorChats = chats.map((chat) =>
          chat.id === currentChatId
            ? {
                ...chat,
                messages: [...chat.messages, userMessage, fallbackMessage],
              }
            : chat
        );
        setChats(errorChats);
        await saveChats(errorChats);
      }
    } finally {
      setLoading(false);
      setAbortController(null);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("Image too large (max 5MB)");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const copyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const startRecording = async () => {
    // Try Web Speech API first for both languages
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = currentLanguage === 'st' ? 'st-ZA' : 'en-US'; // Sesotho (South Africa) or English
      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onstart = () => {
        setVoiceState(prev => ({ ...prev, mode: 'recording', duration: 0 }));
        const timer = setInterval(() => {
          setVoiceState(prev => ({ ...prev, duration: prev.duration + 1 }));
        }, 1000);
        timerRef.current = timer;
      };

      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript) {
          setInput(prev => prev + (prev ? ' ' : '') + finalTranscript);
        }
      };

      recognition.onerror = async (event: any) => {
        console.error('Speech recognition error:', event.error);
        
        // If Web Speech fails for Sesotho, fallback to backend STT
        if (currentLanguage === 'st' && (event.error === 'language-not-supported' || event.error === 'no-speech')) {
          console.log('Web Speech failed for Sesotho, trying backend STT...');
          await startBackendRecording();
          return;
        }
        
        setVoiceState(prev => ({ ...prev, mode: 'idle', duration: 0 }));
        if (timerRef.current) clearInterval(timerRef.current);
      };

      recognition.onend = () => {
        setVoiceState(prev => ({ ...prev, mode: 'idle', duration: 0 }));
        if (timerRef.current) clearInterval(timerRef.current);
        setRecognition(null);
      };

      recognition.start();
      setRecognition(recognition);
    } else {
      // No Web Speech API, use backend
      await startBackendRecording();
    }
  };
  
  const startBackendRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        
        try {
          const response = await fetch(`${import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000'}/api/speech-to-text`, {
            method: 'POST',
            body: formData
          });
          
          const data = await response.json();
          if (data.text && data.text.trim()) {
            setInput(prev => prev + (prev ? ' ' : '') + data.text);
          }
        } catch (error) {
          console.error('Backend speech-to-text failed:', error);
        }
        
        // Cleanup
        stream.getTracks().forEach(track => track.stop());
        setMediaRecorder(null);
      };
      
      recorder.start(100);
      setMediaRecorder(recorder);
      setVoiceState(prev => ({ ...prev, mode: 'recording', duration: 0 }));
      
      const timer = setInterval(() => {
        setVoiceState(prev => ({ ...prev, duration: prev.duration + 1 }));
      }, 1000);
      
      timerRef.current = timer;
    } catch (error) {
      console.error('Backend recording failed:', error);
    }
  };

  const stopRecording = () => {
    if (recognition) {
      recognition.stop();
    }
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setVoiceState(prev => ({ ...prev, mode: 'idle', duration: 0 }));
  };

  const speakMessage = useCallback(
    (text: string, messageId: string) => {
      if (ttsState.speaking && ttsState.currentMessageId === messageId) {
        speechSynthesis.cancel();
        setTTSState((prev) => ({
          ...prev,
          speaking: false,
          currentMessageId: null,
        }));
        return;
      }

      speechSynthesis.cancel();
      const cleanText = text
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1");
      const utterance = new SpeechSynthesisUtterance(cleanText);

      const selectedVoice = ttsState.voices.find(
        (v) => v.name === ttsState.selectedVoice
      );
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      utterance.rate = 0.8;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      utterance.onstart = () => {
        setTTSState((prev) => ({
          ...prev,
          speaking: true,
          currentMessageId: messageId,
        }));
      };

      utterance.onend = () => {
        setTTSState((prev) => ({
          ...prev,
          speaking: false,
          currentMessageId: null,
        }));
      };

      speechSynthesis.speak(utterance);
    },
    [
      ttsState.speaking,
      ttsState.currentMessageId,
      ttsState.voices,
      ttsState.selectedVoice,
    ]
  );

  return (
    <div className="flex w-full bg-white" data-ai-panel style={{height: 'calc(100vh - 200px)'}}>
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? "w-64 sm:w-64" : "w-0"
        } transition-all duration-300 bg-gray-100 border-r border-gray-300 flex flex-col overflow-hidden hidden sm:flex`}
      >
        <div className="p-4 border-b border-gray-300">
          <button
            onClick={createNewChat}
            className="flex items-center w-full gap-3 p-3 text-gray-800 transition-colors bg-white border border-gray-400 rounded-lg hover:bg-gray-50"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm text-gray-800">New chat</span>
          </button>
        </div>

        <div className="flex-1 p-2 overflow-y-auto">
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`group mb-1 ${
                currentChatId === chat.id
                  ? "bg-green-100 border border-green-300"
                  : "hover:bg-gray-200"
              } rounded-lg`}
            >
              <div className="flex items-center">
                <button
                  onClick={() => setCurrentChatId(chat.id)}
                  className="flex-1 p-3 text-left transition-colors"
                >
                  <div className="text-sm text-gray-800 truncate">
                    {chat.title}
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {chat.messages.length} messages
                  </div>
                </button>
                <div className="flex items-center gap-1 pr-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={() => {
                      setEditingChatId(chat.id);
                      setEditingTitle(chat.title);
                    }}
                    className="p-1 text-gray-600 rounded hover:bg-gray-300"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => confirmDeleteChat(chat.id)}
                    className="p-1 text-red-500 rounded hover:bg-red-100"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              {editingChatId === chat.id && (
                <div className="px-3 pb-3">
                  <input
                    type="text"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        updateChatTitle(chat.id, editingTitle);
                        setEditingChatId(null);
                      } else if (e.key === "Escape") {
                        setEditingChatId(null);
                      }
                    }}
                    onBlur={() => {
                      updateChatTitle(chat.id, editingTitle);
                      setEditingChatId(null);
                    }}
                    className="w-full px-2 py-1 text-xs text-gray-800 bg-white border border-gray-400 rounded"
                    autoFocus
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-green-200 sm:p-4 bg-green-50">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 sm:p-2 hover:bg-green-100 rounded-lg transition-colors hidden sm:block"
            >
              <Menu className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-green-800 sm:text-lg">
                AgroSense AI
              </h1>
              <p className="hidden text-xs text-green-600 sm:text-sm sm:block">
                {currentLanguage === "st"
                  ? "Moeletsi oa Temo oa Lesotho"
                  : "Agricultural Expert for Lesotho"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() =>
                setCurrentLanguage(currentLanguage === "en" ? "st" : "en")
              }
              className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 transition-colors"
            >
              <Globe className="inline w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">
                {currentLanguage === "en" ? "English" : "Sesotho"}
              </span>
            </button>
            <button
              onClick={() => setResearchMode(!researchMode)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium border rounded-md transition-colors ${
                researchMode
                  ? "text-green-700 bg-green-100 border-green-300 hover:bg-green-200"
                  : "text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200"
              }`}
            >
              <Search className="inline w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">
                Research {researchMode ? "ON" : "OFF"}
              </span>
            </button>
            {isOffline && (
              <div className="flex items-center gap-1 px-1.5 sm:px-2 py-1 text-xs text-orange-700 bg-orange-100 border border-orange-300 rounded-md">
                <WifiOff className="w-3 h-3" />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 p-2 space-y-4 overflow-y-auto sm:p-4 sm:space-y-6 bg-green-25" style={{height: 'calc(100vh - 350px)'}}>
          {currentChat?.messages.length === 0 ? (
            <div className="py-6 text-center sm:py-12">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 text-xl font-bold text-white bg-green-600 rounded-full sm:w-16 sm:h-16 sm:text-2xl sm:mb-4">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <h2 className="px-4 mb-2 text-lg font-semibold text-green-800 sm:text-2xl">
                {currentLanguage === "st"
                  ? "Nka u thusa joang kajeno?"
                  : "How can I help you today?"}
              </h2>
              <p className="px-4 text-sm text-green-600 sm:text-base">
                {currentLanguage === "st"
                  ? "Mpotse eng kapa eng mabapi le temo Lesotho"
                  : "Ask me anything about farming in Lesotho"}
              </p>
            </div>
          ) : (
            currentChat?.messages.map((message) => (
              <div key={message.id} className="group">
                <div
                  className={`flex gap-2 sm:gap-4 ${
                    message.role === "user" ? "justify-end" : ""
                  }`}
                >
                  {message.role === "assistant" && (
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-semibold text-white bg-green-600 rounded-full sm:w-8 sm:h-8 sm:text-sm">
                      AI
                    </div>
                  )}

                  <div
                    className={`flex-1 max-w-3xl ${
                      message.role === "user" ? "text-right" : ""
                    }`}
                  >
                    <div
                      className={`inline-block p-3 sm:p-4 rounded-2xl max-w-full break-words ${
                        message.role === "user"
                          ? "bg-green-600 text-white ml-6 sm:ml-12"
                          : "bg-gray-50 text-black border border-gray-200"
                      }`}
                    >
                      {message.image && (
                        <img
                          src={message.image}
                          alt="User upload"
                          className="max-w-full mb-2 rounded-lg"
                        />
                      )}
                      {message.role === "assistant" ? (
                        <div className="space-y-3">
                          {message.provider && (
                            <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                              <div className="flex items-center gap-2">
                                <Zap className="w-3 h-3" />
                                <span>Powered by {message.provider}</span>
                              </div>
                              {researchMode && (
                                <span className="px-1 py-0.5 bg-green-100 text-green-600 rounded text-xs">
                                  <Search className="inline w-2 h-2 mr-1" />
                                  Research Enhanced
                                </span>
                              )}
                            </div>
                          )}
                          <div className="prose-sm prose break-words max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-strong:text-gray-900 prose-strong:font-bold prose-p:text-gray-800 prose-li:text-gray-800 prose-blockquote:border-green-500 prose-blockquote:bg-green-50 prose-blockquote:text-green-800">
                            <Markdown remarkPlugins={[remarkGfm]}>
                              {message.content}
                            </Markdown>
                          </div>
                          {message.sources && message.sources.length > 0 && (
                            <div className="pt-3 mt-4 border-t border-gray-200">
                              <p className="flex items-center gap-1 mb-3 text-xs font-semibold text-gray-700">
                                <Search className="w-3 h-3" />
                                Research Sources ({message.sources.length}):
                              </p>
                              <div className="space-y-2">
                                {message.sources
                                  .slice(0, 6)
                                  .map((source, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-start gap-2"
                                    >
                                      <span className="text-xs text-gray-400 mt-0.5 font-mono">
                                        [{idx + 1}]
                                      </span>
                                      <a
                                        href={source}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                        title={source}
                                      >
                                        {
                                          source
                                            .replace(/^https?:\/\//, "")
                                            .split("/")[0]
                                        }
                                      </a>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="break-words whitespace-pre-wrap">
                          {message.content}
                        </div>
                      )}
                    </div>

                    {message.role === "assistant" && (
                      <div className="flex items-center gap-2 mt-2 transition-opacity opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => copyMessage(message.content)}
                          className="p-1 transition-colors rounded hover:bg-green-200"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            speakMessage(message.content, message.id)
                          }
                          className={`p-1 hover:bg-green-200 rounded transition-colors ${
                            ttsState.speaking &&
                            ttsState.currentMessageId === message.id
                              ? "bg-green-200 text-green-700"
                              : ""
                          }`}
                        >
                          {ttsState.speaking &&
                          ttsState.currentMessageId === message.id ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Volume2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {message.role === "user" && (
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-semibold text-white bg-green-600 rounded-full sm:w-8 sm:h-8 sm:text-sm">
                      U
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {loading && (
            <div className="flex gap-2 sm:gap-4">
              <div className="flex items-center justify-center w-6 h-6 text-xs font-semibold text-white bg-green-600 rounded-full sm:w-8 sm:h-8 sm:text-sm">
                AI
              </div>
              <div className="flex-1">
                <div className="p-3 border border-green-200 bg-green-50 sm:p-4 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <span className="ml-2 text-xs text-green-700 sm:text-sm">
                        {currentLanguage === "st"
                          ? "E patlisisa mehloli ea temo le ho nahana..."
                          : "Researching agricultural sources and analyzing..."}
                      </span>
                    </div>
                    <button
                      onClick={stopGeneration}
                      className="px-3 py-1 text-xs text-red-600 transition-colors bg-red-100 rounded-lg hover:bg-red-200"
                    >
                      {currentLanguage === "st" ? "Emisa" : "Stop"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}


          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-2 border-t border-green-200 sm:p-4 bg-green-50">
          {image && (
            <div className="relative mb-3">
              <img
                src={image}
                alt="Preview"
                className="object-cover h-24 rounded-lg"
              />
              <button
                onClick={() => setImage(null)}
                className="absolute p-1 text-gray-600 bg-white rounded-full top-1 right-1 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="flex items-end max-w-5xl gap-2 mx-auto sm:gap-3">
            <div className="relative flex-1">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  currentLanguage === "st"
                    ? "Romela molaetsa ho AgroSense AI..."
                    : "Message AgroSense AI..."
                }
                className="w-full p-3 pr-12 text-sm border-2 border-gray-300 resize-none sm:p-4 sm:pr-14 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-base"
                rows={1}
                style={{ minHeight: "48px", maxHeight: "200px" }}
              />

              <div className="absolute flex items-center gap-1 right-12 sm:right-14 bottom-2 sm:bottom-3 sm:gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  ref={fileInputRef}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-colors"
                  disabled={loading}
                >
                  <ImageIcon className="w-4 h-4" />
                </button>

                {voiceState.mode === "idle" ? (
                  <button
                    onClick={startRecording}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-colors"
                    disabled={loading}
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2 border border-red-200 rounded-lg bg-red-50">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="font-mono text-sm text-red-700">
                      {Math.floor(voiceState.duration / 60)
                        .toString()
                        .padStart(2, "0")}
                      :{(voiceState.duration % 60).toString().padStart(2, "0")}
                    </span>
                    <button
                      onClick={stopRecording}
                      className="p-1 text-gray-600 rounded hover:bg-gray-100"
                      title="Stop"
                    >
                      <Square className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleSend}
                disabled={(!input.trim() && !image) || loading}
                className={`absolute right-2 sm:right-3 bottom-2 sm:bottom-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                  (!input.trim() && !image) || loading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-800"
                }`}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="px-2 mt-2 text-xs text-center text-green-600">
            {currentLanguage === "st"
              ? `AgroSense AI e sebelisa ${
                  researchMode
                    ? "lipatlisiso tsa nako ea nnete ho tsoa mehloling e tšepahalang"
                    : "tsebo ea AI"
                }. Mehloli e bonts'itsoe ka tlase.`
              : `AgroSense AI uses ${
                  researchMode
                    ? "real-time research from trusted agricultural sources"
                    : "AI knowledge"
                }. Sources are cited below responses.`}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deletingChatId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-w-sm p-6 mx-4 bg-white rounded-lg">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">
              {currentLanguage === "st" ? "Hlakola Puisano?" : "Delete Chat?"}
            </h3>
            <p className="mb-4 text-gray-600">
              {currentLanguage === "st"
                ? "Puisano ena e tla hlakoloa ka ho sa feleng. A u na le bonnete?"
                : "This chat will be permanently deleted. Are you sure?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingChatId(null)}
                className="px-4 py-2 text-gray-600 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {currentLanguage === "st" ? "Hlakola" : "Cancel"}
              </button>
              <button
                onClick={() => deleteChat(deletingChatId)}
                className="px-4 py-2 text-white transition-colors bg-red-500 rounded-lg hover:bg-red-600"
              >
                {currentLanguage === "st" ? "Hlakola" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
